<template>
  <div>
    <es-form :padding="{ padding: '0 1.15rem 1.15rem' }">
      <template #es-form>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('DISTRICT_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('DISTRICT_CODE')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <es-select
                api="districts"
                keyValue="code"
                ref="resetDistricts"
                search-min-length="2"
                :error-msg="errorMsgDistrict"
                :has-error="errorDistrict"
                :pre-selected="districtCodes"
                :search="true"
                :value-tpl-fn="(result) => `${result.location.name} (${result.code})`"
                @update:values="districts = $event; onSelectUpdate('districts')"
              />
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('SCHOOL_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('SCHOOL_CODE')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div
                v-if="isAllSchools"
                class="field"
              >
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    value="All Schools"
                    disabled
                  >
                </div>
              </div>
              <es-select
                v-else
                api="schools"
                keyValue="code"
                ref="resetSchools"
                search-min-length="2"
                :error-msg="errorMsgSchool"
                :has-error="errorSchool"
                :api-query-params="getDistrictParams"
                :pre-selected="schoolCodesPromises"
                :search="true"
                :sub-value-tpl-fn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                :value-tpl-fn="(result) => `${result.location.name} (${result.code})`"
                @update:values="schools = $event; onSelectUpdate('schools')"
              />
            </div>
          </div>
        </div>
        <div class="row removeUnderline" v-if='dmPersonTypeMatching'>
          <div class="field is-horizontal">
            <div class="field-label is-normal">
             <label class="label">
                Person Type
              </label>
            </div>
            <div class="field-body">
              <es-select
                v-on:update:values="personTypes = $event; onSelectUpdate('personTypes')"
                valueTpl="${name}"
                keyValue="code"
                ref="resetPersonTypes"
                :error-msg="errorMsgPersonType"
                :has-error="errorPersonType"
                :preSelected=null
                :setValues="sortedPersonTypes"
              />
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                Match Type
              </label>
            </div>
            <div class="field-body">
              <div
                class="field"
                v-if="!enableMatchTypeSelection"
              >
                <div class="control">
                  <div class="select">
                    <select
                      disabled
                      style="width: 214.75px"
                    >
                    </select>
                  </div>
                </div>
              </div>
              <es-select
                v-else
                v-on:update:values="matchTypes = $event; onSelectUpdate('matchTypes')"
                key-value="programCode"
                ref="resetMatchTypes"
                :disabled="!personTypes.length && dmPersonTypeMatching"
                :error-msg="errorMsgMatchType"
                :has-error="errorMatchType"
                :setValues="filterProgramsByPersonType"
                :value-tpl-fn=" (result) => `${result.program.description}`"
              />
            </div>
          </div>
        </div>
      </template>
    </es-form>
    <!-- Person Details -->
    <es-form>
      <template #es-form-header>
        <div class="header-container">
          <div class="header-elements">
            <font-awesome-icon :icon="['fas', 'user']"/>
          </div>
          <div class="header-elements">
            <h6 class="title is-6">
              Person Details
            </h6>
          </div>
        </div>
      </template>
      <template #es-form>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('FIRST_NAME') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('FIRST_NAME')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="firstName"
                    :class="{ 'is-danger': errorFirstName }"
                    @keyup.prevent="onInputKeyup('firstName')"
                  />
                </div>
                <span v-if ="errorFirstName" class="help is-danger">
                  {{ errorMsgFirstName }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('MIDDLE_NAME') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('MIDDLE_NAME')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="middleName"
                    :class="{ 'is-danger': errorMiddleName }"
                    @keyup.prevent="onInputKeyup('middleName')"
                  />
                </div>
                <span v-if ="errorMiddleName" class="help is-danger">
                  {{ errorMsgMiddleName }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('LAST_NAME') }}
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="lastName"
                    :class="{ 'is-danger': errorLastName }"
                    @keyup.prevent="onInputKeyup('lastName')"
                  />
                </div>
                <span v-if ="errorLastName" class="help is-danger">
                  {{ errorMsgLastName }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('PREVIOUS_LAST_NAME') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('PREVIOUS_LAST_NAME')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="altLastName"
                    :class="{ 'is-danger': errorAltLastName }"
                    @keyup.prevent="onInputKeyup('altLastName')"
                  />
                  <span v-if ="errorAltLastName" class="help is-danger">
                    {{ errorMsgAltLastName }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('SUFFIX') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('SUFFIX')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="suffix"
                    :class="{ 'is-danger': errorSuffix }"
                    @keyup.prevent="onInputKeyup('suffix')"
                  />
                </div>
                <span v-if ="errorSuffix" class="help is-danger">
                  {{ errorMsgSuffix }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('GENDER_CODE') }}
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <div class="buttons has-addons">
                    <button
                      v-for="(gender, index) in activeGenders"
                      class="button button--form"
                      style="min-width: 100px"
                      :class="{
                        'is-active': selectedGender === gender.code,
                        'is-danger-border': errorGender,
                      }"
                      :key="index"
                      @click="selectedGender = gender.code; validateField('gender')"
                    >
                      {{ gender.description }}
                    </button>
                  </div>
                </div>
                <span v-if ="errorGender" class="help is-danger">
                  {{ errorMsgGender }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('DATE_OF_BIRTH') }}
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input calendar"
                    type="date"
                    v-model="dob"
                    :class="{ 'is-danger': errorDob }"
                    @change="validateField('dob')"
                  />
                </div>
                <span v-if ="errorDob" class="help is-danger">
                  {{ errorMsgDob }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('ETHNICITY_INDICATOR') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('ETHNICITY_INDICATOR')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    v-model="ethnicity"
                    ref="resetEthnicity"
                    :error-msg="errorMsgEthnicity"
                    :has-error="errorEthnicity"
                    :setValues="ethnicityOptions"
                    :value-tpl-fn="(result) => result.description"
                    @update:values="
                      ethnicity = Array.isArray($event) && $event.length
                        ? $event[0].code
                        : null;
                      onSelectUpdate('ethnicity');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('RACE_ETHNIC_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('RACE_ETHNIC_CODE')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    v-bind:clearOut="true"
                    v-model="raceEthnicCode"
                    ref="resetRaceEthnicCode"
                    :error-msg="errorMsgRaceEthnicCode"
                    :has-error="errorRaceEthnicCode"
                    :setValues="availableRaceEthnicCode"
                    :value-tpl-fn="(result) => result.description"
                    :disabled="!isRaceEthnicCodeEnabled"
                    @update:values="
                      raceEthnicCode = Array.isArray($event) && $event.length
                        ? $event[0].code
                        : null;
                      if (!raceEthnicCode) {
                        race2 = null;
                        race3 = null;
                        race4 = null;
                        race5 = null;
                      }
                      onSelectUpdate('raceEthnicCode');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('RACE2_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('RACE2_CODE')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    ref="resetRace2"
                    v-bind:clearOut="true"
                    v-model="race2"
                    :error-msg="errorMsgRace2"
                    :has-error="errorRace2 && isRace2Enabled"
                    :setValues="availableRace2"
                    :value-tpl-fn="(result) => result.description"
                    :disabled="!isRace2Enabled"
                    @update:values="
                      race2 = Array.isArray($event) && $event.length ? $event[0].code : null;
                      if (!race2) {
                        race3 = null;
                        race4 = null;
                        race5 = null;
                      }
                      onSelectUpdate('race2');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('RACE3_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('RACE3_CODE')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    v-bind:clearOut="true"
                    v-model="race3"
                    ref="resetRace3"
                    :error-msg="errorMsgRace3"
                    :has-error="errorRace3 && isRace3Enabled"
                    :setValues="availableRace3"
                    :value-tpl-fn="(result) => result.description"
                    :disabled="!isRace3Enabled"
                    @update:values="
                      race3 = Array.isArray($event) && $event.length ? $event[0].code : null;
                      if (!race3) {
                        race4 = null;
                        race5 = null;
                      }
                      onSelectUpdate('race3');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('RACE4_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('RACE4_CODE')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    v-bind:clearOut="true"
                    v-model="race4"
                    ref="resetRace4"
                    :error-msg="errorMsgRace4"
                    :has-error="errorRace4 && isRace4Enabled"
                    :setValues="availableRace4"
                    :value-tpl-fn="(result) => result.description"
                    :disabled="!isRace4Enabled"
                    @update:values="
                      race4 = Array.isArray($event) && $event.length ? $event[0].code : null;
                      if (!race4) {
                        race5 = null;
                      }
                      onSelectUpdate('race4');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('RACE5_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('RACE5_CODE')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    v-bind:clearOut="true"
                    v-model="race5"
                    ref="resetRace5"
                    :error-msg="errorMsgRace5"
                    :has-error="errorRace5 && isRace5Enabled"
                    :setValues="availableRace5"
                    :value-tpl-fn="(result) => result.description"
                    :disabled="!isRace5Enabled"
                    @update:values="
                      race5 = Array.isArray($event) && $event.length ? $event[0].code : null;
                      onSelectUpdate('race5');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('FULL_NAME') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('FULL_NAME')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="fullName"
                    :class="{ 'is-danger': errorFullName }"
                    @keyup.prevent="onInputKeyup('fullName')"
                  />
                </div>
                <span v-if ="errorFullName" class="help is-danger">
                  {{ errorMsgFullName }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('SOCIAL_SECURITY_NUMBER')"
                >
                   &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="ssn"
                    placeholder="###-##-####"
                    :class="{ 'is-danger': errorSSN }"
                    @keypress="isSSN($event)"
                    @keyup.prevent="validateSsn"
                    @blur="ssnFocusOut"
                    @focus="ssnFocusIn"
                  />
                </div>
                <span v-if ="errorSSN" class="help is-danger">
                  {{ errorMsgSSN }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('LOCAL_STUDENT_ID') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('LOCAL_STUDENT_ID')"
                >
                    &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-danger': errorLocalId }"
                    type="text"
                    v-model="localId"
                    @keyup.prevent="onInputKeyup('localId')"
                  />
                </div>
                <span v-if ="errorLocalId" class="help is-danger">
                  {{ errorMsgLocalId }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('SOURCE_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('SOURCE_CODE')"
                >
                    &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    ref="resetSource"
                    v-bind:clearOut="true"
                    v-model="source"
                    :error-msg="errorMsgSource"
                    :has-error="errorSource"
                    :setValues="activeSources"
                    :value-tpl-fn="(result) => result.description"
                    :pre-selected="source ? [{ code: source }] : []"
                    @update:values="
                      source = Array.isArray($event) && $event.length
                        ? $event[0].code
                        : null;
                      onSelectUpdate('source');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('STATE_STUDENT_ID') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('STATE_STUDENT_ID')"
                >
                    &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-danger': errorStateId }"
                    type="text"
                    v-model="stateId"
                    @keyup.prevent="onInputKeyup('stateId')"
                  />
                </div>
                <span v-if ="errorStateId" class="help is-danger">
                  {{ errorMsgStateId }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('GRADE_CODE') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if= "!fieldRequired('GRADE_CODE')"
                >
                    &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    ref="resetGrade"
                    v-bind:clearOut="true"
                    v-model="grade"
                    :error-msg="errorMsgGrade"
                    :has-error="errorGrade"
                    :setValues="activeGrades"
                    :value-tpl-fn="(result) => result.description"
                    :pre-selected="grade ? [{ code: grade }] : []"
                    @update:values="
                      grade = Array.isArray($event) && $event.length
                        ? $event[0].code
                        : null;
                      onSelectUpdate('grade');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </es-form>
    <!-- Address -->
    <es-form>
      <template #es-form-header>
        <div class="header-container">
          <div class="header-elements">
            <font-awesome-icon :icon="['fas', 'user']"/>
          </div>
          <div class="header-elements">
            <h6 class="title is-6">
              Address
            </h6>
          </div>
        </div>
      </template>
      <template #es-form>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('STREET_1') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if="!fieldRequired('STREET_1')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-danger': errorAddress1 }"
                    type="text"
                    v-model="address1"
                    @keyup.prevent="onInputKeyup('address1')"
                  />
                </div>
                <span v-if ="errorAddress1" class="help is-danger">
                  {{ errorMsgAddress1 }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('STREET_2') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if="!fieldRequired('STREET_2')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-danger': errorAddress2 }"
                    type="text"
                    v-model="address2"
                    @keyup.prevent="onInputKeyup('address2')"
                  />
                </div>
                <span v-if ="errorAddress2" class="help is-danger">
                  {{ errorMsgAddress2 }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('CITY') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if="!fieldRequired('CITY')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-danger': errorCity }"
                    type="text"
                    v-model="city"
                    @keyup.prevent="onInputKeyup('city')"
                  />
                </div>
                <span v-if ="errorCity" class="help is-danger">
                  {{ errorMsgCity }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('STATE_CODE') }}
                <span class="label-optional has-text-grey-light">
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    keyValue="code"
                    ref="resetState"
                    v-bind:clearOut="true"
                    v-model="state"
                    :error-msg="errorMsgState"
                    :has-error="errorState"
                    :setValues="activeStates"
                    :value-tpl-fn="(result) => result.description"
                    :pre-selected="state ? [{ code: state }] : []"
                    @update:values="
                      state = Array.isArray($event) && $event.length
                        ? $event[0].code
                        : null;
                      onSelectUpdate('state');
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                {{ fieldLabel('ZIP') }}
                <span
                  class="label-optional has-text-grey-light"
                  v-if="!fieldRequired('ZIP')"
                >
                  &nbsp;(optional)
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{ 'is-danger': errorZip }"
                    type="text"
                    v-model="zip"
                    @keyup.prevent="onInputKeyup('zip')"
                  />
                </div>
                <span v-if ="errorZip" class="help is-danger">
                  {{ errorMsgZip }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </es-form>
    <div
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click="reset"
        style="margin-right: 1rem"
        :disabled="loadingMatch"
      >
        Reset
      </button>
      <button
        class="button is-primary"
        @click="checkForm"
        :disabled="loadingMatch"
      >
        {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import {
  mapGetters, mapActions,
} from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsSelect from '@/components/EsSelect.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';

library.add(
  faUser,
);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    NotificationManager,
  ],
  components: {
    EsForm,
    EsSelect,
  },
  props: {
    // value coming from router to help return the match type id for the give matchTypeCode.
    matchTypeCode: {
      type: String,
    },
    dmPersonTypeMatching: {
      type: Boolean,
    },
    personType: {
      type: String,
    },
    sortedPersonTypes: {
      type: Array,
    },
  },
  data() {
    return {
      // form values
      address1: null,
      address2: null,
      altLastName: null,
      city: null,
      districtCodes: [], // single district users to pass in distictCode to es-selct
      districts: [], // slected districts
      dob: null,
      ethnicity: null,
      ethnicityOptions: [],
      firstName: null,
      fullName: null,
      grade: null,
      lastName: null,
      localId: null,
      matchTypes: [], // selected match types
      middleName: null,
      raceEthnicCode: null,
      race2: null,
      race3: null,
      race4: null,
      race5: null,
      schoolCodes: [], // single school users to setup pre-selected data in es-select
      schools: [], // selectes schools
      selectedGender: null,
      source: null,
      ssn: null,
      state: null,
      stateId: null,
      suffix: null,
      zip: null,

      // loading values
      loadingMatch: false,

      // form validation
      errorDistrict: false,
      errorSchool: false,
      errorDob: false,
      errorFirstName: false,
      errorMiddleName: false,
      errorGender: false,
      errorGrade: false,
      errorLastName: false,
      errorAltLastName: false,
      errorSuffix: false,
      errorFullName: false,
      errorLocalId: false,
      errorStateId: false,
      errorAddress1: false,
      errorAddress2: false,
      errorCity: false,
      errorZip: false,
      errorState: false,
      errorMatchType: false,
      errorPersonType: false,
      errorEthnicity: false,
      errorRaceEthnicCode: false,
      errorRace2: false,
      errorRace3: false,
      errorRace4: false,
      errorRace5: false,
      errorSource: false,
      errorSSN: false,

      errorMsgDistrict: null,
      errorMsgSchool: null,
      errorMsgMatchType: null,
      errorMsgPersonType: null,
      errorMsgGender: null,
      errorMsgFirstName: null,
      errorMsgLastName: null,
      errorMsgAltLastName: null,
      errorMsgSuffix: null,
      errorMsgFullName: null,
      errorMsgState: null,
      errorMsgLocalId: null,
      errorMsgStateId: null,
      errorMsgAddress1: null,
      errorMsgAddress2: null,
      errorMsgCity: null,
      errorMsgZip: null,
      errorMsgEthnicity: null,
      errorMsgRaceEthnicCode: null,
      errorMsgRace2: null,
      errorMsgRace3: null,
      errorMsgRace4: null,
      errorMsgRace5: null,
      errorMsgSource: null,
      errorMsgGrade: null,
      errorMsgDob: null,
      errorMsgSSN: null,

      ssnTemporary: null,
      personTypes: [],
      programs: [],
    };
  },
  watch: {
    race2(newVal) {
      if (newVal === null) {
        this.race3 = null;
        if (this.$refs.resetRace3 && typeof this.$refs.resetRace3.clear === 'function') {
          this.$refs.resetRace3.clear();
        }
        this.race4 = null;
        if (this.$refs.resetRace4 && typeof this.$refs.resetRace4.clear === 'function') {
          this.$refs.resetRace4.clear();
        }
        this.race5 = null;
        if (this.$refs.resetRace5 && typeof this.$refs.resetRace5.clear === 'function') {
          this.$refs.resetRace5.clear();
        }
      }
    },
    race3(newVal) {
      if (newVal === null) {
        this.race4 = null;
        if (this.$refs.resetRace4 && typeof this.$refs.resetRace4.clear === 'function') {
          this.$refs.resetRace4.clear();
        }
        this.race5 = null;
        if (this.$refs.resetRace5 && typeof this.$refs.resetRace5.clear === 'function') {
          this.$refs.resetRace5.clear();
        }
      }
    },
    race4(newVal) {
      if (newVal === null) {
        this.race5 = null;
        if (this.$refs.resetRace5 && typeof this.$refs.resetRace5.clear === 'function') {
          this.$refs.resetRace5.clear();
        }
      }
    },
    raceEthnicCode(newVal) {
      if (newVal === null) {
        this.race2 = null;
        if (this.$refs.resetRace2 && typeof this.$refs.resetRace2.clear === 'function') {
          this.$refs.resetRace2.clear();
        }
        this.race3 = null;
        if (this.$refs.resetRace3 && typeof this.$refs.resetRace3.clear === 'function') {
          this.$refs.resetRace3.clear();
        }
        this.race4 = null;
        if (this.$refs.resetRace4 && typeof this.$refs.resetRace4.clear === 'function') {
          this.$refs.resetRace4.clear();
        }
        this.race5 = null;
        if (this.$refs.resetRace5 && typeof this.$refs.resetRace5.clear === 'function') {
          this.$refs.resetRace5.clear();
        }
      }
    },
  },
  async created() {
    this.loadPrograms();
    this.setDataElements();
    this.checkUserTypes();
    await this.loadPersonTypes();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters([
      'isDistrictUser',
      'isSingleDistrictUser',
      'isSingleDistrictUser',
      'isSingleSchoolUser',
      'isStateUser',
      'userDistricts',
      'userSchools',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldLengthsByPersonType',
      'getFieldRequiredForPersonType',
    ]),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeGrades',
      'activeMatchTypes',
      'activeRaces',
      'activeSources',
      'activeStates',
    ]),
    availableRaceEthnicCode() {
      const selectedRaces = [
        this.race2,
        this.race3,
        this.race4,
        this.race5,
      ];
      return this.buildRaceList(selectedRaces);
    },
    availableRace2() {
      const selectedRaces = [
        this.raceEthnicCode,
        this.race3,
        this.race4,
        this.race5,
      ];
      return this.buildRaceList(selectedRaces);
    },
    availableRace3() {
      const selectedRaces = [
        this.raceEthnicCode,
        this.race2,
        this.race4,
        this.race5,
      ];
      return this.buildRaceList(selectedRaces);
    },
    availableRace4() {
      const selectedRaces = [
        this.raceEthnicCode,
        this.race2,
        this.race3,
        this.race5,
      ];
      return this.buildRaceList(selectedRaces);
    },
    availableRace5() {
      const selectedRaces = [
        this.raceEthnicCode,
        this.race2,
        this.race3,
        this.race4,
      ];
      return this.buildRaceList(selectedRaces);
    },
    enableMatchTypeSelection() {
      //  If there is default value in District or School field, or the user selects a specific
      // district or school, enable match types.

      // cases to cover:
      // 1. district and school selected

      if (this.districts.length && this.schools.length) return true;
      return false;
    },
    formData() {
      const body = {};
      body.matchTypeId = this.matchTypeId;
      body.districtCode = (
        Array.isArray(this.districts)
        && this.districts.length
        && this.districts[0]
      ) ? this.districts[0].code : null;
      body.schoolCode = (
        Array.isArray(this.schools)
        && this.schools.length
        && this.schools[0]
      ) ? this.schools[0].code : null;
      body.programCode = (
        Array.isArray(this.matchTypes)
        && this.matchTypes.length
        && this.matchTypes[0]
      ) ? this.matchTypes[0].programCode : null;
      body.student = this.studentObject;
      return body;
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);

      return params;
    },
    isAllSchools() {
      // Show disabled All Schools input when:
      // 1. User is state user and no districts selected
      // 2. User is not state user, not single school user, and no districts selected
      return (this.isStateUser && !this.districts.length)
        || (!this.isStateUser && !this.isSingleSchoolUser && !this.districts.length);
    },
    isRaceEthnicCodeEnabled() {
      return true;
    },
    isRace2Enabled() {
      return !!this.raceEthnicCode;
    },
    isRace3Enabled() {
      return !!this.race2;
    },
    isRace4Enabled() {
      return !!this.race3;
    },
    isRace5Enabled() {
      return !!this.race4;
    },
    matchTypeId() {
      const found = this.activeMatchTypes.find(m => m.code === this.matchTypeCode);
      return found ? found.id : null;
    },
    matchTypeQueryParams() {
      const params = { matchEnabled: true };
      if (this.districts.length) {
        params.distictCode = this.districts[0].code;
      }
      return params;
    },
    filterProgramsByPersonType() {
      if (this.dmPersonTypeMatching === false) {
        return this.programs;
      }
      const selectedPersonType = this.personTypes.map(type => type.code);
      return this.programs.filter(
        program => selectedPersonType.includes(program.program.personTypeCode),
      );
    },
    studentObject() {
      const student = {};

      // Only set districtCode if available
      student.districtCode = (
        Array.isArray(this.districts)
        && this.districts.length
        && this.districts[0]
      ) ? this.districts[0].code : null;
      student.firstName = this.firstName;
      student.middleName = this.middleName;
      student.lastName = this.lastName;
      student.previousLastName = this.altLastName;
      student.suffix = this.suffix;
      student.genderCode = this.selectedGender;
      student.dateOfBirth = this.dob ? moment(this.dob).format('YYYY-MM-DD') : null;
      student.ethnicityIndicator = this.ethnicity;
      student.raceEthnicCode = this.raceEthnicCode;
      student.race2Code = this.race2;
      student.race3Code = this.race3;
      student.race4Code = this.race4;
      student.race5Code = this.race5;
      student.fullName = this.fullName;
      student.socialSecurityNumber = this.ssn;
      student.localId = this.localId;
      student.sourceCode = this.source;
      student.stateStudentId = this.stateId;
      student.gradeCode = this.grade;
      student.street1 = this.address1;
      student.street2 = this.address2;
      student.city = this.city;
      student.stateCode = this.state;
      student.zip = this.zip;

      return student;
    },
    validForm() {
      return (
        !this.errorFirstName
        && !this.errorMiddleName
        && !this.errorLastName
        && !this.errorAltLastName
        && !this.errorFullName
        && !this.errorLocalId
        && !this.errorStateId
        && !this.errorSuffix
        && !this.errorAddress1
        && !this.errorAddress2
        && !this.errorCity
        && !this.errorZip
        && !this.errorDistrict
        && !this.errorSchool
        && !this.errorGender
        && !this.errorDob
        && !this.errorRaceEthnicCode
        && !this.errorSource
        && !this.errorGrade
        && !this.errorMatchType
        && !this.errorSSN
        && !this.errorEthnicity
        && !this.errorRace2
        && !this.errorRace3
        && !this.errorRace4
        && !this.errorRace5
        && !this.errorState
      );
    },
  },
  asyncComputed: {
    async schoolCodesPromises() {
      const promises = [];
      this.schoolCodes.forEach(code => promises
        .push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async loadPrograms() {
      try {
        const res = await axios.get(`matchtypes/${this.matchTypeId}/programs?matchEnabled=true`);
        this.programs = res.data;
      } catch (error) {
        this.$root.$emit('setLoadingError', true);
      }
    },
    buildRaceList(selectedRaces) {
      return this.activeRaces.filter(race => !selectedRaces.includes(race.code));
    },
    checkForm() {
      const fieldsToValidate = [
        'firstName',
        'middleName',
        'lastName',
        'altLastName',
        'suffix',
        'fullName',
        'localId',
        'stateId',
        'address1',
        'address2',
        'city',
        'zip',
        'gender',
        'dob',
        // Add more field names here as needed
      ];
      fieldsToValidate.forEach((field) => {
        if (field === 'gender' || field === 'dob') {
          this.validateField(field);
        } else {
          this.validateInputField(field);
        }
      });

      const selectFieldsToValidate = [
        'districts',
        'schools',
        'matchTypes',
        'personTypes',
        'ethnicity',
        'raceEthnicCode',
        'race2',
        'race3',
        'race4',
        'race5',
        'source',
        'grade',
        'state',
        // Add more field names here as needed
      ];
      selectFieldsToValidate.forEach(field => this.validateSelectField(field));

      this.validateSsn();

      if (this.validForm) {
        this.hideNotification();
        this.match();
      } else {
        this.showNotification('danger',
          ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
    },
    checkUserDistricts() {
      // if the user is a single-distrit-user then set selectedDistrictType to Specific Districts
      // and assign districtCodes to a list of codes from the store to pass into es-select.
      if (this.isSingleDistrictUser) { // single-district
        this.districtCodes = [this.userDistricts[0].code];
        this.districts = this.districtCodes.map(d => ({ code: d }));
      }
    },
    checkSingleSchoolUser() {
      // if the user is a single-school-user then assign schoolCodes to school:district String
      // from the store to prepare schoolCodePromises computed prop.
      if (this.isSingleSchoolUser) {
        this.schoolCodes = [`${this.userSchools[0].code}:${this.userDistricts[0].code}`];
        this.schools = this.schoolCodes
          .map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] }));
      }
    },
    // check initial district and school permissions to set default data selections
    checkUserTypes() {
      this.checkUserDistricts();
      this.checkSingleSchoolUser();
    },
    getPersonTypeCode() {
      if (this.personType) return this.personType;
      if (this.personTypes && this.personTypes.length > 0 && this.personTypes[0].code) {
        return this.personTypes[0].code;
      }
      return null;
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'DMATCH', this.getPersonTypeCode());
    },
    fieldLength(code) {
      return this.getFieldLengthsByPersonType(code, 'DMATCH', this.getPersonTypeCode());
    },
    fieldRequired(code) {
      return this.getFieldRequiredForPersonType(code, 'DMATCH', this.getPersonTypeCode());
    },
    async match() {
      try {
        this.loadingMatch = true;
        this.$root.$emit('dmMatchOptionsLoading', true); // Match Results loader
        this.$root.$emit('dmMatchOptionsLoadingError', false); // Match Results error message

        const postResponse = await axios.post('/directmatch/transactions', this.formData);
        const getResponse = await axios.get(`directmatch/transactions/${postResponse.data.requestId}`);
        this.$emit('transResponse', [getResponse.data]); // emit transaction data up to parent level
      } catch (e) {
        this.showNotification('danger', [e.response.data.message]);
        this.$root.$emit('dmMatchOptionsLoadingError', true); // Match Results error message
      } finally {
        this.loadingMatch = false;
        this.$root.$emit('dmMatchOptionsLoading', false); // Match Results loader
      }
    },
    onInputKeyup(field) {
      if (!this[field]) {
        // If the field is empty, clear the error
        const errorKey = `error${field.charAt(0).toUpperCase()}${field.slice(1)}`;
        const errorMsgKey = `errorMsg${field.charAt(0).toUpperCase()}${field.slice(1)}`;
        this[errorKey] = false;
        this[errorMsgKey] = null;
      } else {
        // Only validate if the user starts typing something
        this.validateInputField(field);
      }
    },
    onSelectUpdate(field) {
      // value is expected to be the new value (array for multi, array or null for single)
      const isEmpty = Array.isArray(this[field]) ? this[field].length === 0 : !this[field];
      if (isEmpty) {
        const errorKey = `error${field.charAt(0).toUpperCase()}${field.slice(1)}`;
        const errorMsgKey = `errorMsg${field.charAt(0).toUpperCase()}${field.slice(1)}`;
        this[errorKey] = false;
        this[errorMsgKey] = null;
      } else {
        this.validateSelectField(field);
      }
    },
    reset() {
      this.hideNotification();

      // Reset all user-editable fields and their error states/messages
      // 1. Form values
      this.address1 = null;
      this.address2 = null;
      this.altLastName = null;
      this.city = null;
      this.districtCodes = [];
      this.districts = [];
      if (this.$refs.resetDistricts && typeof this.$refs.resetDistricts.clear === 'function') {
        this.$refs.resetDistricts.clear();
      }
      this.dob = null;
      this.ethnicity = null;
      if (this.$refs.resetEthnicity && typeof this.$refs.resetEthnicity.clear === 'function') {
        this.$refs.resetEthnicity.clear();
      }
      this.firstName = null;
      this.fullName = null;
      this.grade = null;
      if (this.$refs.resetGrade && typeof this.$refs.resetGrade.clear === 'function') {
        this.$refs.resetGrade.clear();
      }
      this.lastName = null;
      this.localId = null;
      this.matchTypes = [];
      if (this.$refs.resetMatchTypes && typeof this.$refs.resetMatchTypes.clear === 'function') {
        this.$refs.resetMatchTypes.clear();
      }
      this.middleName = null;
      this.personTypes = [];
      if (this.$refs.resetPersonTypes && typeof this.$refs.resetPersonTypes.clear === 'function') {
        this.$refs.resetPersonTypes.clear();
      }
      this.raceEthnicCode = null;
      if (this.$refs.resetRaceEthnicCode && typeof this.$refs.resetRaceEthnicCode.clear === 'function') {
        this.$refs.resetRaceEthnicCode.clear();
      }
      this.race2 = null;
      if (this.$refs.resetRace2 && typeof this.$refs.resetRace2.clear === 'function') {
        this.$refs.resetRace2.clear();
      }
      this.race3 = null;
      if (this.$refs.resetRace3 && typeof this.$refs.resetRace3.clear === 'function') {
        this.$refs.resetRace3.clear();
      }
      this.race4 = null;
      if (this.$refs.resetRace4 && typeof this.$refs.resetRace4.clear === 'function') {
        this.$refs.resetRace4.clear();
      }
      this.race5 = null;
      if (this.$refs.resetRace5 && typeof this.$refs.resetRace5.clear === 'function') {
        this.$refs.resetRace5.clear();
      }
      this.schoolCodes = [];
      this.schools = [];
      if (this.$refs.resetSchools && typeof this.$refs.resetSchools.clear === 'function') {
        this.$refs.resetSchools.clear();
      }
      this.selectedGender = null;
      this.source = null;
      if (this.$refs.resetSource && typeof this.$refs.resetSource.clear === 'function') {
        this.$refs.resetSource.clear();
      }
      this.ssn = null;
      this.ssnTemporary = null;
      this.state = null;
      if (this.$refs.resetState && typeof this.$refs.resetState.clear === 'function') {
        this.$refs.resetState.clear();
      }
      this.stateId = null;
      this.suffix = null;
      this.zip = null;

      // 2. Error flags
      this.errorDistrict = false;
      this.errorSchool = false;
      this.errorDob = false;
      this.errorFirstName = false;
      this.errorMiddleName = false;
      this.errorGender = false;
      this.errorGrade = false;
      this.errorLastName = false;
      this.errorAltLastName = false;
      this.errorSuffix = false;
      this.errorFullName = false;
      this.errorLocalId = false;
      this.errorStateId = false;
      this.errorAddress1 = false;
      this.errorAddress2 = false;
      this.errorCity = false;
      this.errorZip = false;
      this.errorState = false;
      this.errorMatchType = false;
      this.errorPersonType = false;
      this.errorEthnicity = false;
      this.errorRaceEthnicCode = false;
      this.errorRace2 = false;
      this.errorRace3 = false;
      this.errorRace4 = false;
      this.errorRace5 = false;
      this.errorSource = false;
      this.errorSSN = false;

      // 3. Error messages
      this.errorMsgDistrict = null;
      this.errorMsgSchool = null;
      this.errorMsgMatchType = null;
      this.errorMsgPersonType = null;
      this.errorMsgGender = null;
      this.errorMsgFirstName = null;
      this.errorMsgLastName = null;
      this.errorMsgAltLastName = null;
      this.errorMsgSuffix = null;
      this.errorMsgFullName = null;
      this.errorMsgState = null;
      this.errorMsgLocalId = null;
      this.errorMsgStateId = null;
      this.errorMsgAddress1 = null;
      this.errorMsgAddress2 = null;
      this.errorMsgCity = null;
      this.errorMsgZip = null;
      this.errorMsgEthnicity = null;
      this.errorMsgRaceEthnicCode = null;
      this.errorMsgRace2 = null;
      this.errorMsgRace3 = null;
      this.errorMsgRace4 = null;
      this.errorMsgRace5 = null;
      this.errorMsgSource = null;
      this.errorMsgGrade = null;
      this.errorMsgDob = null;
      this.errorMsgSSN = null;
    },
    setDataElements() {
      this.ethnicityOptions = [
        { code: '1', description: 'Hispanic' },
        { code: '2', description: 'Non-Hispanic' },
        { code: '3', description: 'Unknown' },
      ];
    },
    validateField(field) {
      if (field === 'gender') {
        if (this.selectedGender) {
          this.errorGender = false;
          this.errorMsgGender = null;
        } else {
          this.errorGender = true;
          this.errorMsgGender = `${this.fieldLabel('GENDER_CODE')
          } is required.`;
        }
      }
      if (field === 'dob') {
        if (!this.dob) {
          this.errorDob = true;
          this.errorMsgDob = `${this.fieldLabel('DATE_OF_BIRTH')
          } is required.`;
        } else if (this.dob && !this.isValidDateFormat(this.dob, 'YYYY-MM-DD')) {
          this.errorDob = true;
          this.errorMsgDob = `${this.fieldLabel('DATE_OF_BIRTH')
          } is invalid.`;
        } else if (this.dob && !this.futureBirthYearsAllowed
          && !this.isValidDOB(this.dob, 'YYYY-MM-DD')) {
          this.errorDob = true;
          this.errorMsgDob = `${this.fieldLabel('DATE_OF_BIRTH')
          } is invalid.`;
        } else if (this.dob && this.futureBirthYearsAllowed
          && !this.isValidDOBWithfutureBirthYearsAllowed(this.dob, 'YYYY-MM-DD')) {
          this.errorDob = true;
          this.errorMsgDob = `${this.fieldLabel('DATE_OF_BIRTH')
          } is invalid.`;
        } else {
          this.errorDob = false;
          this.errorMsgDob = '';
        }
      }
    },
    validateSelectField(field) {
      const ObjEsSelect = {
        districts: ['DISTRICT_CODE', 'errorDistrict', 'errorMsgDistrict', 'array'],
        schools: ['SCHOOL_CODE', 'errorSchool', 'errorMsgSchool', 'array'],
        matchTypes: [null, 'errorMatchType', 'errorMsgMatchType', 'array'],
        personTypes: [null, 'errorPersonType', 'errorMsgPersonType', 'array'],
        ethnicity: ['ETHNICITY_INDICATOR', 'errorEthnicity', 'errorMsgEthnicity', 'scalar'],
        raceEthnicCode: ['RACE_ETHNIC_CODE', 'errorRaceEthnicCode', 'errorMsgRaceEthnicCode', 'scalar'],
        race2: ['RACE2_CODE', 'errorRace2', 'errorMsgRace2', 'scalar'],
        race3: ['RACE3_CODE', 'errorRace3', 'errorMsgRace3', 'scalar'],
        race4: ['RACE4_CODE', 'errorRace4', 'errorMsgRace4', 'scalar'],
        race5: ['RACE5_CODE', 'errorRace5', 'errorMsgRace5', 'scalar'],
        source: ['SOURCE_CODE', 'errorSource', 'errorMsgSource', 'scalar'],
        grade: ['GRADE_CODE', 'errorGrade', 'errorMsgGrade', 'scalar'],
        state: ['STATE_CODE', 'errorState', 'errorMsgState', 'scalar'],
      };
      if (Object.keys(ObjEsSelect).includes(field)) {
        const [code, error, errorMsg, type] = ObjEsSelect[field];

        // For dependent race fields, only validate if enabled
        if (
          (field === 'race2' && !this.isRace2Enabled)
          || (field === 'race3' && !this.isRace3Enabled)
          || (field === 'race4' && !this.isRace4Enabled)
          || (field === 'race5' && !this.isRace5Enabled)
        ) {
          this[error] = false;
          this[errorMsg] = null;
          return;
        }

        if (field === 'matchTypes' || field === 'personTypes') {
          // Always required, static error message
          if (!Array.isArray(this[field]) || this[field].length === 0) {
            this[error] = true;
            this[errorMsg] = field === 'matchTypes'
              ? 'Match Type is required.'
              : 'Person Type is required.';
          } else {
            this[error] = false;
            this[errorMsg] = null;
          }
        } else if (type === 'array') {
          if (
            this.fieldRequired(code)
            && (!Array.isArray(this[field]) || this[field].length === 0)
          ) {
            this[error] = true;
            this[errorMsg] = `${this.fieldLabel(`${code}`)} is required.`;
          } else {
            this[error] = false;
            this[errorMsg] = null;
          }
        } else if (type === 'scalar') {
          if (
            this.fieldRequired(code)
            && (this[field] === null || this[field] === undefined || this[field] === '')
          ) {
            this[error] = true;
            this[errorMsg] = `${this.fieldLabel(`${code}`)} is required.`;
          } else {
            this[error] = false;
            this[errorMsg] = null;
          }
        }
      }
    },
    validateInputField(field) {
      const makeField = (code, hasErrorKey, msgKey) => [
        code, hasErrorKey, msgKey, this.fieldLength(code),
      ];

      const Obj = {
        // Person Information Validation
        firstName: makeField('FIRST_NAME', 'errorFirstName', 'errorMsgFirstName'),
        middleName: makeField('MIDDLE_NAME', 'errorMiddleName', 'errorMsgMiddleName'),
        lastName: makeField('LAST_NAME', 'errorLastName', 'errorMsgLastName'),
        altLastName: makeField('PREVIOUS_LAST_NAME', 'errorAltLastName', 'errorMsgAltLastName'),
        suffix: makeField('SUFFIX', 'errorSuffix', 'errorMsgSuffix'),
        fullName: makeField('FULL_NAME', 'errorFullName', 'errorMsgFullName'),
        localId: makeField('LOCAL_STUDENT_ID', 'errorLocalId', 'errorMsgLocalId'),
        stateId: makeField('STATE_STUDENT_ID', 'errorStateId', 'errorMsgStateId'),
        gender: makeField('GENDER_CODE', 'errorGender', 'errorMsgGender'),
        dob: makeField('DATE_OF_BIRTH', 'errorDob', 'errorMsgDob'),

        // Contact Information Validation
        address1: makeField('STREET_1', 'errorAddress1', 'errorMsgAddress1'),
        address2: makeField('STREET_2', 'errorAddress2', 'errorMsgAddress2'),
        city: makeField('CITY', 'errorCity', 'errorMsgCity'),
        zip: makeField('ZIP', 'errorZip', 'errorMsgZip'),
      };

      if (Object.keys(Obj).includes(field)) {
        const [code, error, errorMsg, maxLength] = Obj[field];
        if (this.fieldRequired(code) && !this[field]) {
          this[error] = true;
          this[errorMsg] = `${this.fieldLabel(code)} is required.`;
        } else if (this[field] && this[field].length > maxLength) {
          this[error] = true;
          this[errorMsg] = `${this.fieldLabel(code)} allows a maximum of ${maxLength} characters.`;
        } else {
          this[error] = false;
          this[errorMsg] = null;
        }
      }
    },
    validateSsn() {
      if (this.fieldRequired('SOCIAL_SECURITY_NUMBER') && (!this.ssn || this.ssn === '')) {
        this.errorSSN = true;
        this.errorMsgSSN = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} is required.`;
      } else if (this.ssn && this.ssn.replaceAll('-', '').length !== 9) {
        this.errorSSN = true;
        this.errorMsgSSN = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} is invalid.`;
      } else {
        // Remove all errors if the SSN is emptied by the user as it is an optional field
        this.errorSSN = false;
        this.errorMsgSSN = null;
      }
    },
    isSSN(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 83) {
        evt.preventDefault();
      }
    },
    ssnFocusIn() {
      if (this.ssnTemporary !== null) {
        // Unformat SSN value before user starts modifying it
        this.ssn = this.ssnTemporary.toString();
      }
    },
    ssnFocusOut() {
      if (this.ssn !== null) {
        // Reformat the SSN after ignoring the character 'S' and the digits in user input
        this.ssnTemporary = parseFloat(this.ssn.replace(/[S0-9]/g, '')); // eslint-disable-line
      }
      if (Number.isNaN(this.ssnTemporary)) {
        // Ensure that value is not NaN. If so, initialize it to 'this.ssn (NULL)'.
        // This happens if user provides a blank input
        this.ssnTemporary = this.ssn;
      }
      if (this.ssnTemporary !== null) {
        // Format display value of SSN
        this.ssn = (this.ssnTemporary.replace(/.(?=.{6}$)/g, '$&-')).replace(/.(?=.{4}$)/g, '$&-');
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .is-danger-border
    border-color: #ff3860 !important
</style>
